import cookies from 'js-cookie';
import {
  APP_ID_DEV, APP_ID_PROD, CYPRESS_COOKIE_NAME, HEAP_TAG_NAME,
  INVALID_DOMAINS, PROD_EXT_ACCOUNT_COMMENT,
  USER_DETAILS_KEYS
} from './const';
import { getEnv } from './util/envUtil';
import { processSubscriptionsObj } from './util/subscriptionsUtil';
import { generateAddUserPropertiesScript, sanitizeHeapUserProperties } from './util/userPropertiesUtil';

/**
 * This function initializes heap only if the email is in the correct group for tracking and if
 * a cypress cookie is not present
 *
 * Parameter object should look like this:
 * {
 *   userDetails: userDetailsObject,
 *   subscriptions: subscriptionDetailsObject
 *   featureFlags: featureFlagsObject
 * }
 *
 * @param {object} heapTrackingData
 * @returns {void}
 */
export const initializeHeap = (heapTrackingData = {}) => {
  const isCypress = Boolean(getCookie(CYPRESS_COOKIE_NAME));
  const { userDetails, subscriptions, featureFlags } = heapTrackingData;
  const sanitizedUserDetails = sanitizeUserDetailsObj(userDetails);
  if (!isCypress) {
    const currentEnv = getEnv();
    const isExternalUser = isExternalEmail(sanitizedUserDetails.email);
    const appId = deriveAppId(currentEnv, isExternalUser);
    const initializationData = {
      appId,
      isExternalUser,
      userDetails: sanitizedUserDetails,
      featureFlags,
      subscriptions
    };
    initializeAndLoadHeapScriptTag(initializationData);
  }
};

/**
 * This function will delete the heap object which
 * stops data from being sent to heap.
 */
export function tearDown() {
  if (window.heap) {
    delete window.heap;
  }
  if (checkIfHeapTagAdded(HEAP_TAG_NAME)) {
    const heapTag = document.getElementById(HEAP_TAG_NAME);
    document.head.removeChild(heapTag);
  }
}

/**
 * This function checks for Heap's existence and wraps the heap.track method
 * @param {string} eventName
 * @param {object} eventProperties
 */
export const trackHeapEvent = (eventName, eventProperties = {}) => {
  const heapClient = getHeapClient();
  if (!heapClient) {
    return;
  }
  heapClient.track(eventName, eventProperties);
};

const getHeapClient = () => {
  return window.heap;
};

/**
 * Ensures that we have all of the default user params that we need for analytics
 * @param {Object} userDetails
 */
export const sanitizeUserDetailsObj = (userDetails) => {
  const userDetailsBase = (typeof userDetails === 'undefined') ? {} : deriveUserIdFromLegacyOwnerId(userDetails);
  const keysToPick = USER_DETAILS_KEYS;
  const sanitizedObjEntries = new Map();
  keysToPick.forEach(key => {
    const value = userDetailsBase[key] ? userDetailsBase[key] : undefined;
    sanitizedObjEntries.set(key, value);
  });
  return Object.fromEntries(sanitizedObjEntries);
};

/**
 * If the legacyUserId exists, use that instead of the id
 * @param userDetails
 */
export const deriveUserIdFromLegacyOwnerId = (userDetails) => {
  if (userDetails.legacyOwnerId) {
    userDetails.id = userDetails.legacyOwnerId;
  }
  return userDetails;
};

/**
 * We define the additional user properties for heap in this function. It is explicitly spelled out instead of
 * passed in as a JSON object so that there is no confusion as to what parameters we are using.
 * @param {object} heapParams
 * @param {object} UserData
 */

export const initializeAndLoadHeapScriptTag = ({
  appId,
  isExternalUser,
  userDetails,
  featureFlags,
  subscriptions
}) => {
  const tagName = HEAP_TAG_NAME;
  const isHeapPrevAdded = checkIfHeapTagAdded(tagName);
  const currentEnv = getEnv();
  const { uuid } = userDetails;
  const hasSubscriptions = Boolean(subscriptions);
  const hasFieldviewSubscription = processSubscriptionsObj(subscriptions);
  const heapUserProperties = sanitizeHeapUserProperties({
    userDetails,
    featureFlags,
    hasSubscriptions,
    hasFieldviewSubscription
  });
  const scriptLines = [];
  if (!isHeapPrevAdded) {
    /* eslint-disable */
    scriptLines.push(`window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};`);
    scriptLines.push(`heap.load(${appId});`);

    // We won't be able to use the prod account until later, but this hook is there so that we can see when it will eventually be used
    if (willProdAccountBeUsed(currentEnv, isExternalUser)) {
      scriptLines.push(`// ${PROD_EXT_ACCOUNT_COMMENT}`);
    }

    if (uuid) {
      scriptLines.push(`heap.identify('${uuid}');`);
      scriptLines.push(generateAddUserPropertiesScript(heapUserProperties));
    } else {
      // If we can't identify the user, at least add the build_environment
      scriptLines.push(`
        heap.addUserProperties(
            {
              build_environment: '${currentEnv}'
            }
        );
      `);
    }
    const script = scriptLines.join('\n');
    const newScript = document.createElement('script');
    newScript.setAttribute('id', tagName);
    newScript.type = 'text/javascript';
    const inlineScript = document.createTextNode(script);
    newScript.appendChild(inlineScript);
    document.head.appendChild(newScript);
  }
}

/**
 * Looks for the heap tag if it already exists and returns true if it does
 * @returns {boolean}
 */
export const checkIfHeapTagAdded = (tag) => {
  return document.getElementById(tag) ? true : false;
}

/**
 * Determines if the email passed in is a valid domain for tracking in heap
 * @param email
 * @returns {boolean}
 */
export const isExternalEmail = (email) => {
  const invalidDomains = INVALID_DOMAINS;
  if (email) {
    return !invalidDomains.some(element => email.includes(element));
  } else {
    return true;
  }
};

/**
 * Checks to see if the cypress cookie exists
 * @param cookieName
 * @returns {mixed} null if cookie doesn't exist, value string if it does
 */
export const getCookie = (cookieName) => {
  return cookies.get(cookieName);
};

/**
 * For now this will only return the dev app id. In the future it will be switched to the prod app id.
 * We can also later add a hook here which will return a dev app id if the environment is not production, but
 * there will be further discussion about that.
 * @param {string} currentEnv
 * @param {boolean} isExternalUser
 * @returns {number} App ID used for Heap
 */
export const deriveAppId = (currentEnv, isExternalUser) => {
  if (willProdAccountBeUsed(currentEnv, isExternalUser)) {
    return APP_ID_PROD;
  }
  return APP_ID_DEV;
}

/**
 * Abstracted function for determining if the prod account will later be used.
 * @param {string} currentEnv
 * @param {boolean} isExternalUser
 * @returns {boolean}
 */
export const willProdAccountBeUsed = (currentEnv, isExternalUser) => {
  return currentEnv == 'production' && isExternalUser;
};
